import { Badge, Button, Card, Flex, Grid, Link, Separator, Text } from "@radix-ui/themes";
import { Link as InertiaLink } from "@inertiajs/react";
import { Calendar, ClipboardList, MapPin, Tag } from "lucide-react";
import { TrackManagerSeason } from "@/types/shared";
import { badgeColors } from "@/shared/constants/constants";

interface Props {
  season: TrackManagerSeason;
  /** Rendered inside a mobile card that already shows this record's identity, so
   *  drop whatever the card is already saying. Each panel keeps what the card can't. */
  compact?: boolean;
}

interface CountCardProps {
  icon: React.ReactNode;
  label: string;
  count: number | string;
  color?: string;
}

function CountCard({ icon, label, count, color }: CountCardProps) {
  return (
    <Card>
      <Flex direction="column" gap="1">
        <Flex align="center" gap="1" style={{ color: color ?? "inherit" }}>
          {icon}
          <Text size="1" color="gray">
            {label}
          </Text>
        </Flex>
        <Text size="6" weight="bold" style={{ color: color ?? "inherit" }}>
          {count}
        </Text>
      </Flex>
    </Card>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function SeasonDetailPanel({ season, compact = false }: Props) {
  const publicPath = `/tracks/${season.track_slug}/seasons/${season.slug}`;

  return (
    <Flex direction="column" gap="4">
      {/* The card already shows the name, the track and both badges, and "View
          Public Page" below is the public link — nothing here it doesn't have. */}
      {!compact && (
        <>
          <Flex direction="column" gap="2">
            <Link href={publicPath} size="5" weight="bold">
              {season.name}
            </Link>
            <Flex align="center" gap="1">
              <MapPin size={14} />
              <Text size="2" color="gray">
                {season.track_name}
              </Text>
            </Flex>
            <Flex align="center" gap="2" wrap="wrap">
              <Badge color={badgeColors[season.status]} variant="soft">
                <Text style={{ textTransform: "capitalize" }}>{season.status}</Text>
              </Badge>
              <Badge color={badgeColors[season.phase]} variant="soft">
                <Text style={{ textTransform: "capitalize" }}>{season.phase}</Text>
              </Badge>
            </Flex>
          </Flex>

          <Separator size="4" />
        </>
      )}

      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">
          Overview
        </Text>
        <Grid columns="2" gap="2">
          <CountCard
            icon={<Calendar size={14} />}
            label="Start"
            count={formatDate(season.start_date)}
          />
          <CountCard
            icon={<Calendar size={14} />}
            label="End"
            count={formatDate(season.end_date)}
          />
          <CountCard
            icon={<ClipboardList size={14} />}
            label="Events"
            count={season.event_count}
          />
          <CountCard
            icon={<Tag size={14} />}
            label="Title Sponsor"
            count={season.title_sponsor || "—"}
          />
        </Grid>
      </Flex>

      <Separator size="4" />
      <Grid columns="2" gap="2">
        <Button asChild color="gray" variant="soft" size="2">
          <InertiaLink href={`/track_manager/seasons/${season.slug}/edit`}>
            Edit Season
          </InertiaLink>
        </Button>
        <Button asChild variant="soft" size="2">
          <InertiaLink href={`/track_manager/seasons/${season.slug}/events`}>
            Manage Events
          </InertiaLink>
        </Button>
        <Button asChild variant="soft" color="gray" size="2">
          <a href={publicPath}>View Public Page</a>
        </Button>
      </Grid>
    </Flex>
  );
}
